import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleInfo,
  faHeartPulse,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";

import MainTitle from "../../components/MainTitle";
import Feature from "../../components/Feature";
import useAuth from "../../hooks/useAuth";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

const emergencySymptoms = [
  "Severe difficulty breathing",
  "Severe chest pain",
  "Severe abdominal pain",
  "Severe or uncontrolled bleeding",
  "Suspected heart attack or stroke",
  "An infection with dizziness, weakness or confusion (sepsis)",
  "Acute back pain with paralysis of the legs or difficulty using the bladder or bowel",
  "Unexplained loss of consciousness",
  "Uncontrolled seizures or fits",
  "You are pregnant and experiencing pain, bleeding or reduced movement of your baby",
];

const commonSymptoms = [
  "Stress or Feeling Overwhelmed",
  "Anxiety or Excessive Worry",
  "Low Mood",
  "Sleep Problems",
  "Fatigue or Low Energy",
  "Physical Pain or Discomfort",
  "Recovering from Illness or Injury",
  "Weight Changes or Nutrition Concerns",
  "Reduced Motivation",
  "Difficulty Concentrating",
  "Feeling Isolated or Lonely",
  "Family or Relationship Difficulties",
  "Work-related Stress",
  "Women's Health or Menopause Concerns",
  "Not Sure / Something Doesn't Feel Right",
];

function WellbeingSignposting() {
  const axiosPrivate = useAxiosPrivate();
  const { auth, userPolicy } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [mentalHealthCrisis, setMentalHealthCrisis] = useState(null);
  const [medicalSymptoms, setMedicalSymptoms] = useState(null);
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [startError, setStartError] = useState("");
  const [processingMessage, setProcessingMessage] = useState(
    "System is processing your request.",
  );
  const [assistantMessage, setAssistantMessage] = useState("");
  const [currentQuestion, setCurrentQuestion] = useState({});
  const [assistantSingleSelection, setAssistantSingleSelection] = useState("");
  const [assistantMultiSelections, setAssistantMultiSelections] = useState([]);
  const [assistantAnswerError, setAssistantAnswerError] = useState("");
  const [assistantAnswerSubmitted, setAssistantAnswerSubmitted] =
    useState(false);
  const [assistantData, setAssistantData] = useState(null);
  const [yesNoAnswer, setYesNoAnswer] = useState(null);

  const canContinueToSymptoms =
    mentalHealthCrisis === false && medicalSymptoms === false;
  const shouldShowEmergencyMessage =
    mentalHealthCrisis === true || medicalSymptoms === true;

  const toggleSymptom = (symptom) => {
    setSelectedSymptoms((current) => {
      if (current.includes(symptom)) {
        return current.filter((item) => item !== symptom);
      }

      if (current.length >= 3) {
        return current;
      }

      return [...current, symptom];
    });
  };

  const goToSymptomsStep = () => {
    if (canContinueToSymptoms) {
      setCurrentStep(2);
    }
  };

  const startSignpostingSession = async () => {
    if (selectedSymptoms.length === 0) return;

    setIsProcessing(true);
    setProcessingMessage("System is processing your request.");
    setStartError("");
    setAssistantMessage("");

    try {
      const response = await axiosPrivate.post("/signposting/start", {
        symptoms: selectedSymptoms,
      });

      setProcessingMessage("System is generating the next question.");
      setAssistantMessage(
        response?.data?.assistant_message ||
        response?.data?.recommendations?.message,
      );

      setCurrentStep(3);

      handleAssistantResponse(response?.data);
      setAssistantData({
        conversationId: response?.data?.conversation_id,
        safetyIdentifier: response?.data?.safety_identifier,
      });
    } catch (error) {
      setStartError(
        error?.response?.data?.message ||
        "We could not start the session just now. Please try again.",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAssistantResponse = (data) => {
    const output = JSON.parse(data?.assistant_message);
    console.log(output.response_type);
    if (output.response_type === "resolution") {
      setCurrentStep(4);
      setAssistantMessage(output);
      return;
    }

    setCurrentQuestion({
      question_label: output.question_label,
      response_type: output.response_type,
      options: output.options,
    });

    setAssistantSingleSelection("");
    setAssistantMultiSelections([]);
    setAssistantAnswerError("");
    setAssistantAnswerSubmitted(false);
  };

  const toggleAssistantMultiSelection = (value) => {
    setAssistantMultiSelections((current) => {
      if (current.includes(value)) {
        return current.filter((item) => item !== value);
      }

      return [...current, value];
    });
  };

  const hasValidAssistantAnswer =
    currentQuestion.response_type === "single_choice"
      ? assistantSingleSelection !== ""
      : currentQuestion.response_type === "yes_no"
        ? yesNoAnswer !== null
        : assistantMultiSelections.length > 0;

  const submitAssistantAnswer = async () => {
    if (!hasValidAssistantAnswer) {
      setAssistantAnswerError(
        "Please select at least one option before submitting.",
      );
      return;
    }

    const payload = {
      question_label: currentQuestion.question_label,
      safety_identifier: assistantData?.safetyIdentifier,
      response_type: currentQuestion.response_type,
      answer:
        currentQuestion.response_type === "single_choice"
          ? assistantSingleSelection
          : currentQuestion.response_type === "yes_no"
            ? String(yesNoAnswer)
            : assistantMultiSelections,
    };

    setIsProcessing(true);
    setProcessingMessage("System is processing your answer.");
    setAssistantMessage("");
    setAssistantAnswerError("");

    try {
      const response = await axiosPrivate.post("/signposting/answer", {
        conversation_id: assistantData?.conversationId,
        safety_identifier: assistantData?.safetyIdentifier,
        user_answer: payload.answer,
      });

      setAssistantMessage(
        response?.data?.assistant_message ||
        response?.data?.recommendations?.message,
      );

      setCurrentStep(3);

      handleAssistantResponse(response?.data);
    } catch (error) {
      setAssistantAnswerError(
        error?.response?.data?.message ||
        "We could not start the session just now. Please try again.",
      );
    } finally {
      setIsProcessing(false);
    }

    setAssistantAnswerError("");
    setAssistantAnswerSubmitted(true);
  };

  const ProcessingPanel = () => (
    <div className="rounded-xl border border-[#DDCCDF] bg-white p-6 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="mt-1 h-5 w-5 animate-spin rounded-full border-2 border-[#DDD5E1] border-t-[#41264B]" />
        <div>
          <h3 className="text-lg font-semibold text-[#41264B]">
            Processing request
          </h3>
          <p className="mt-1 text-sm text-gray-600">{processingMessage}</p>
          <p className="mt-2 text-xs uppercase tracking-[0.2em] text-gray-400">
            Please keep this page open
          </p>
        </div>
      </div>
    </div>
  );

  const ShowAssistantQuestion = () => {
    switch (currentQuestion.response_type) {
      case "single_choice":
        return (
          <div className="rounded-xl border border-[#DDCCDF] bg-white p-6">
            <h3 className="text-xl font-semibold text-[#41264B]">
              {currentQuestion.question_label}
            </h3>

            <div className="mt-4 space-y-3">
              {currentQuestion.options?.map((option) => {
                const isSelected = assistantSingleSelection === option.value;

                return (
                  <label
                    key={option.value}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition ${isSelected
                      ? "border-[#866393] bg-[#F3E8F7]"
                      : "border-[#E6E0EA] bg-white hover:border-[#c9b3d3]"
                      }`}
                  >
                    <input
                      type="radio"
                      name="assistantSingleChoice"
                      value={option.value}
                      checked={isSelected}
                      onChange={() => {
                        setAssistantSingleSelection(option.value);
                        setAssistantAnswerError("");
                      }}
                      className="mt-1"
                    />
                    <span className="text-sm text-[#41264B]">
                      {option.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      case "multiple_choice":
        return (
          <div className="rounded-xl border border-[#DDCCDF] bg-white p-6">
            <h3 className="text-xl font-semibold text-[#41264B]">
              {currentQuestion.question_label}
            </h3>

            <div className="mt-4 space-y-3">
              {currentQuestion.options?.map((option) => {
                const isSelected = assistantMultiSelections.includes(
                  option.value,
                );

                return (
                  <label
                    key={option.value}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition ${isSelected
                      ? "border-[#866393] bg-[#F3E8F7]"
                      : "border-[#E6E0EA] bg-white hover:border-[#c9b3d3]"
                      }`}
                  >
                    <input
                      type="checkbox"
                      value={option.value}
                      checked={isSelected}
                      onChange={() => {
                        toggleAssistantMultiSelection(option.value);
                        setAssistantAnswerError("");
                      }}
                      className="mt-1"
                    />
                    <span className="text-sm text-[#41264B]">
                      {option.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      case "yes_no":
        return (
          <div className="rounded-xl border border-[#DDCCDF] bg-white p-6">
            <h3 className="text-xl font-semibold text-[#41264B]">
              {currentQuestion.question_label}
            </h3>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="yesNo"
                  value="yes"
                  onChange={() => setYesNoAnswer(true)}
                />{" "}
                Yes
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="yesNo"
                  value="no"
                  onChange={() => setYesNoAnswer(false)}
                />{" "}
                No
              </label>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <section className="max-w-4xl mx-auto p-6 space-y-8 text-portal-dark-blue">
      <Feature featureName="ai-signposting" redirectOnDenied />
      <MainTitle
        title="Not sure what support you need?"
        subtitle="Answer a few quick questions and we'll help
              guide you to the most appropriate services."
        backButton
      />

      <div className="space-y-6">
        {currentStep === 1 && (
          <div className="max-w-4xl mx-auto space-y-8 text-portal-dark-blue">
            <div className="border border-blue-400 bg-blue-50 text-blue-800 p-4 rounded-lg flex items-start gap-3">
              <FontAwesomeIcon icon={faCircleInfo} className="mt-1 text-lg" />
              <p className="text-sm">
                You must not use this service for emergencies or urgent
                conditions, as this may delay necessary treatment.
              </p>
            </div>

            <div>
              <h2 className="font-bold text-lg mb-3">
                Are you experiencing a mental health crisis or in suicidal
                distress?
              </h2>
              <div className="flex gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="mentalCrisis"
                    value="no"
                    onChange={() => setMentalHealthCrisis(false)}
                  />{" "}
                  No
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="mentalCrisis"
                    value="yes"
                    onChange={() => setMentalHealthCrisis(true)}
                  />{" "}
                  Yes
                </label>
              </div>
            </div>

            <div>
              <h2 className="font-bold text-lg mb-3">
                Are you experiencing any of the following medical symptoms?
              </h2>
              <div className="bg-[#F9F9F9] p-4 rounded-lg border">
                <ul className="space-y-2 text-sm text-wba-dark-pink">
                  {emergencySymptoms.map((symptom, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <FontAwesomeIcon icon={faHeartPulse} className="mt-1 " />
                      <span>{symptom}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex gap-6 mt-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="symptoms"
                    value="no"
                    onChange={() => setMedicalSymptoms(false)}
                  />{" "}
                  No
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="symptoms"
                    value="yes"
                    onChange={() => setMedicalSymptoms(true)}
                  />{" "}
                  Yes
                </label>
              </div>
            </div>

            {shouldShowEmergencyMessage && (
              <div className="border border-red-500 bg-red-50 text-red-700 p-4 rounded-lg flex items-start gap-3">
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  className="mt-1 text-lg text-red-600"
                />
                <p className="text-sm">
                  Call 999 in the UK or 112 in Europe now. If you are abroad,
                  please contact your local emergency services immediately.
                </p>
              </div>
            )}

            {canContinueToSymptoms && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={goToSymptomsStep}
                  className="px-4 py-2 bg-hub-primary hover:bg-hub-primary-hover text-white rounded-lg font-medium shadow"
                >
                  Continue
                </button>
              </div>
            )}
          </div>
        )}

        {isProcessing && <ProcessingPanel />}

        {!isProcessing && currentStep === 2 && (
          <div className="rounded-xl border border-[#DDCCDF] bg-white p-6">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-[#41264B]">
                Which of these best matches what you are experiencing?
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Select up to 3 symptoms below that apply.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {commonSymptoms.map((symptom) => {
                const isSelected = selectedSymptoms.includes(symptom);
                const disableOption =
                  !isSelected && selectedSymptoms.length >= 3;

                return (
                  <button
                    key={symptom}
                    type="button"
                    onClick={() => toggleSymptom(symptom)}
                    disabled={disableOption}
                    className={`min-h-[88px] rounded-xl border p-4 text-left text-sm font-medium transition ${isSelected
                      ? "border-[#866393] bg-[#F3E8F7] text-[#41264B] shadow-sm"
                      : disableOption
                        ? "border-[#E6E0EA] bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "border-[#E6E0EA] bg-white text-[#5A3B66] hover:border-[#c9b3d3]"
                      }`}
                  >
                    <span className="block leading-5">{symptom}</span>
                  </button>
                );
              })}
            </div>

            <p className="mt-4 text-sm text-gray-500">
              {selectedSymptoms.length} of {3} selected
            </p>

            {assistantMessage && (
              <div className="mt-4 rounded-lg border border-[#CFE8D8] bg-[#F2FBF5] p-4 text-sm text-[#2F6A43]">
                {assistantMessage}
              </div>
            )}

            {startError && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                {startError}
              </div>
            )}

            {selectedSymptoms.length > 0 && (
              <div className="mt-5 flex justify-end">
                <button
                  type="button"
                  onClick={startSignpostingSession}
                  disabled={isProcessing}
                  className="px-4 py-2 bg-hub-primary hover:bg-hub-primary-hover disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-medium shadow"
                >
                  {isProcessing ? "Processing..." : "Start"}
                </button>
              </div>
            )}
          </div>
        )}

        {/*  */}

        {!isProcessing && currentStep === 3 && (
          <>
            <ShowAssistantQuestion />
            {assistantAnswerError && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {assistantAnswerError}
              </div>
            )}

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={submitAssistantAnswer}
                disabled={isProcessing}
                className="px-4 py-2 bg-hub-primary hover:bg-hub-primary-hover text-white rounded-lg font-medium shadow"
              >
                Submit answer
              </button>
            </div>
          </>
        )}

        {currentStep === 4 && (
          <>
            <div>
              <h2 className="text-xl font-semibold text-[#41264B]">
                Services That May Help
              </h2>
              <p>
                Based on your answers, these services may provide useful support
                and guidance.
              </p>

              <div className="mt-4 space-y-4">
                {assistantMessage?.resolution?.services?.map(
                  (service, index) => (
                    <div
                      key={index}
                      className="rounded-lg border border-[#E6E0EA] bg-white p-4"
                    >
                      <h3 className="text-lg font-semibold text-[#41264B] ">
                        {service.name}
                      </h3>
                      <p className="mt-1 text-sm text-[#5A3B66]">
                        {service.description}
                      </p>
                      <a
                        href={service.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-semibold text-[#41264B] underline"
                      >
                        Learn More
                      </a>
                    </div>
                  ),
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default WellbeingSignposting;
